<template>
    <div class="contents">
        <div class="container-fluid">
            <!-- Breadcrumb Header -->
            <div class="row">
                <div class="col-lg-12">
                    <div class="breadcrumb-main user-member justify-content-sm-between">
                        <div class="d-flex flex-wrap justify-content-center breadcrumb-main__wrapper">
                            <div class="d-flex align-items-center user-member__title justify-content-center mr-sm-25">
                                <h4 class="text-capitalize fw-500 breadcrumb-title">
                                    Detail Shop
                                </h4>
                            </div>
                        </div>
                        <div class="action-btn">
                            <router-link to="/shop" class="btn px-15 btn-secondary text-white">
                                <i class="fas fa-arrow-left fs-16"></i>
                                Kembali
                            </router-link>
                        </div>
                    </div>
                </div>
            </div>
            
            <div class="row" v-if="shop">
                <div class="col-lg-12">
                    <!-- Modern Shop Header -->
                    <div class="shop-header-card">
                        <div class="shop-header-content">
                            <div class="shop-logo-wrapper">
                                <img 
                                    v-if="shop.logo_url" 
                                    :src="shop.logo_url" 
                                    alt="Shop Logo" 
                                    class="shop-logo"
                                />
                                <div v-else class="shop-logo-placeholder">
                                    <i class="fas fa-store"></i>
                                </div>
                            </div>
                            <div class="shop-info">
                                <div class="shop-title-section">
                                    <h2 class="shop-name">{{ shop.name }}</h2>
                                    <div class="shop-slug">
                                        <i class="fas fa-link"></i>
                                        <code>{{ shop.slug }}</code>
                                    </div>
                                </div>
                                <div class="shop-status-section">
                                    <span 
                                        :class="[
                                            'status-badge',
                                            shop.status === 'active' ? 'status-active' : 'status-inactive'
                                        ]"
                                    >
                                        <i :class="shop.status === 'active' ? 'fas fa-check-circle' : 'fas fa-times-circle'"></i>
                                        {{ shop.status === 'active' ? 'Aktif' : 'Tidak Aktif' }}
                                    </span>
                                </div>
                            </div>
                        </div>
                    </div>

                    <!-- Modern Tabs -->
                    <div class="modern-tabs-container">
                        <div class="modern-tabs-header">
                            <button
                                v-for="tab in tabs"
                                :key="tab.id"
                                class="modern-tab-button"
                                :class="{ 'active': activeTab === tab.id }"
                                @click="activeTab = tab.id"
                            >
                                <i :class="tab.icon"></i>
                                <span>{{ tab.label }}</span>
                            </button>
                        </div>

                        <div class="modern-tabs-content">
                            <!-- Tab Statistik -->
                            <div v-show="activeTab === 'statistik'" class="tab-pane-content">
                                <ShopStatistics :shop-id="shop.id" />
                            </div>

                            <!-- Tab Produk -->
                            <div v-show="activeTab === 'produk'" class="tab-pane-content">
                                <ShopProductsTab :shop-id="shop.id" />
                            </div>

                            <!-- Tab Transaksi -->
                            <div v-show="activeTab === 'transaksi'" class="tab-pane-content">
                                <ShopOrdersTab :shop-id="shop.id" />
                            </div>

                            <!-- Tab Pengaturan -->
                            <div v-show="activeTab === 'pengaturan'" class="tab-pane-content">
                                <ShopSettingsTab :shop="shop" @updated="loadShop" />
                            </div>
                        </div>
                    </div>
                </div>
            </div>
            
            <!-- Loading State -->
            <div v-else class="loading-container">
                <div class="spinner-border text-primary" role="status">
                    <span class="sr-only">Loading...</span>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
    import ShopStatistics from "@/components/shop/ShopStatistics.vue";
    import ShopProductsTab from "@/components/shop/ShopProductsTab.vue";
    import ShopOrdersTab from "@/components/shop/ShopOrdersTab.vue";
    import ShopSettingsTab from "@/components/shop/ShopSettingsTab.vue";

    export default {
        components: {
            ShopStatistics,
            ShopProductsTab,
            ShopOrdersTab,
            ShopSettingsTab,
        },
        data() {
            return {
                shop: null,
                activeTab: 'statistik',
                tabs: [
                    { id: 'statistik', label: 'Statistik', icon: 'fas fa-chart-bar' },
                    { id: 'produk', label: 'Produk', icon: 'fas fa-box' },
                    { id: 'transaksi', label: 'Transaksi', icon: 'fas fa-shopping-cart' },
                    { id: 'pengaturan', label: 'Pengaturan', icon: 'fas fa-cog' }
                ]
            };
        },
        mounted() {
            this.loadShop();
            // Check if there's a tab parameter in route
            if (this.$route.query.tab) {
                this.activeTab = this.$route.query.tab;
            }
        },
        methods: {
            async loadShop() {
                try {
                    const shopId = this.$route.params.id;
                    const response = await this.$store.dispatch('shop/shop_detail', shopId);
                    this.shop = response.data.data.shop;
                } catch (error) {
                    this.$toast.error('Gagal memuat data shop');
                    this.$router.push('/shop');
                }
            }
        }
    };
</script>

<style scoped>
/* Modern Shop Header */
.shop-header-card {
    background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
    border-radius: 16px;
    padding: 32px;
    margin-bottom: 24px;
    box-shadow: 0 10px 30px rgba(102, 126, 234, 0.3);
    color: white;
}

.shop-header-content {
    display: flex;
    align-items: center;
    gap: 24px;
}

.shop-logo-wrapper {
    flex-shrink: 0;
}

.shop-logo {
    width: 120px;
    height: 120px;
    border-radius: 16px;
    object-fit: cover;
    border: 4px solid rgba(255, 255, 255, 0.3);
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.2);
    background: white;
}

.shop-logo-placeholder {
    width: 120px;
    height: 120px;
    border-radius: 16px;
    background: rgba(255, 255, 255, 0.2);
    display: flex;
    align-items: center;
    justify-content: center;
    border: 4px solid rgba(255, 255, 255, 0.3);
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.2);
}

.shop-logo-placeholder i {
    font-size: 48px;
    color: rgba(255, 255, 255, 0.8);
}

.shop-info {
    flex: 1;
    display: flex;
    flex-direction: column;
    gap: 16px;
}

.shop-title-section {
    display: flex;
    flex-direction: column;
    gap: 8px;
}

.shop-name {
    font-size: 28px;
    font-weight: 700;
    margin: 0;
    color: white;
    text-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
}

.shop-slug {
    display: flex;
    align-items: center;
    gap: 8px;
    font-size: 14px;
    color: rgba(255, 255, 255, 0.9);
}

.shop-slug i {
    font-size: 12px;
}

.shop-slug code {
    background: rgba(255, 255, 255, 0.2);
    padding: 4px 12px;
    border-radius: 6px;
    color: white;
    font-size: 13px;
    font-weight: 500;
    border: 1px solid rgba(255, 255, 255, 0.3);
}

.shop-status-section {
    display: flex;
    align-items: center;
}

.status-badge {
    display: inline-flex;
    align-items: center;
    gap: 8px;
    padding: 8px 16px;
    border-radius: 20px;
    font-size: 14px;
    font-weight: 600;
    backdrop-filter: blur(10px);
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
}

.status-active {
    background: rgba(16, 185, 129, 0.3);
    border: 1px solid rgba(16, 185, 129, 0.5);
    color: white;
}

.status-inactive {
    background: rgba(239, 68, 68, 0.3);
    border: 1px solid rgba(239, 68, 68, 0.5);
    color: white;
}

/* Modern Tabs */
.modern-tabs-container {
    background: white;
    border-radius: 16px;
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);
    overflow: hidden;
}

.modern-tabs-header {
    display: flex;
    background: #f8f9fa;
    border-bottom: 2px solid #e9ecef;
    padding: 0;
    overflow-x: auto;
    scrollbar-width: none;
    -ms-overflow-style: none;
}

.modern-tabs-header::-webkit-scrollbar {
    display: none;
}

.modern-tab-button {
    flex: 1;
    min-width: 150px;
    padding: 16px 24px;
    background: transparent;
    border: none;
    border-bottom: 3px solid transparent;
    cursor: pointer;
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 10px;
    font-size: 15px;
    font-weight: 500;
    color: #6c757d;
    transition: all 0.3s ease;
    position: relative;
    white-space: nowrap;
}

.modern-tab-button:hover {
    background: rgba(102, 126, 234, 0.05);
    color: #667eea;
}

.modern-tab-button.active {
    color: #667eea;
    background: white;
    border-bottom-color: #667eea;
}

.modern-tab-button.active::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    height: 3px;
    background: linear-gradient(90deg, #667eea 0%, #764ba2 100%);
}

.modern-tab-button i {
    font-size: 16px;
}

.modern-tabs-content {
    padding: 32px;
    min-height: 400px;
}

.tab-pane-content {
    animation: fadeIn 0.3s ease;
}

@keyframes fadeIn {
    from {
        opacity: 0;
        transform: translateY(10px);
    }
    to {
        opacity: 1;
        transform: translateY(0);
    }
}

/* Loading State */
.loading-container {
    display: flex;
    justify-content: center;
    align-items: center;
    min-height: 400px;
    padding: 40px;
}

/* Responsive */
@media (max-width: 768px) {
    .shop-header-card {
        padding: 24px;
    }
    
    .shop-header-content {
        flex-direction: column;
        text-align: center;
    }
    
    .shop-logo,
    .shop-logo-placeholder {
        width: 100px;
        height: 100px;
    }
    
    .shop-name {
        font-size: 24px;
    }
    
    .modern-tabs-header {
        flex-wrap: nowrap;
        overflow-x: auto;
    }
    
    .modern-tab-button {
        min-width: 120px;
        padding: 12px 16px;
        font-size: 14px;
    }
    
    .modern-tab-button span {
        display: none;
    }
    
    .modern-tabs-content {
        padding: 20px;
    }
}
</style>
